import React, { useEffect } from 'react';
import { Spin } from 'antd';
import PropertySearchForm from './PropertySearchForm';
import { useSelector, useDispatch } from 'react-redux';
import { AppState } from 'store';
import {
  usStateSelector,
  driversSelector,
  resultSelector,
  detailsSelector,
  valueSelector,
  detailsLoadingSelector,
} from './PropertySearch.selectors';
import {
  getStates as getStatesAction,
  searchByAddress,
  searchByOwner,
  searchByDrivers,
} from './PropertySearch.actions';
import {
  USStateCodes,
  SearchTypeFormNames,
  LocationFormNames,
  PROPERTY_SEARCH_FORM,
} from './PropertySearch.constants';
import { PropertySearchResultTable } from './templates';
import PropertySearchDetails from './PropertySearchDetails/PropertySearchDetails';
import { getFormValues } from 'redux-form';
import {
  LOADING,
  Sizes,
  loadingSelector,
  ReducerNames,
  scrollTop,
} from 'common';
import { createSelector } from 'reselect';
import { useAccount, useIsAuthenticated, useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { getAccessToken } from 'common/helpers';

const selector = createSelector(
  (state: AppState) => usStateSelector(state),
  (state: AppState) => driversSelector(state),
  (state: AppState) => loadingSelector(ReducerNames.PROPERTY_SEARCH)(state),
  (state: AppState) => resultSelector(state),
  (state: AppState) => detailsSelector(state),
  (state: AppState) => valueSelector(state, LocationFormNames.COUNTY),
  (state: AppState) => valueSelector(state, LocationFormNames.CITY),
  (state: AppState) => getFormValues(PROPERTY_SEARCH_FORM)(state),
  (state: AppState) => detailsLoadingSelector(state),
  (
    usState,
    driversData,
    loading,
    result,
    details,
    countyHob,
    cityHob,
    formValues,
    detailsLoading,
  ) => ({
    usState,
    driversData,
    loading,
    result,
    details,
    countyHob,
    cityHob,
    formValues,
    detailsLoading,
  }),
);

const PropertySearch: React.FC = () => {
  const dispatch = useDispatch();
  const {
    usState,
    loading,
    driversData,
    result,
    details,
    countyHob,
    cityHob,
    formValues,
    detailsLoading,
  } = useSelector(selector);

  const { instance, accounts, inProgress } = useMsal();
  const account = useAccount(accounts[0] || {});
  const isAuthenticated = useIsAuthenticated();

  useEffect(() => {
    if (account && inProgress === InteractionStatus.None && !usState) {
      getAccessToken(instance, account, inProgress).then((response) => {
        if (response?.accessToken)
          dispatch(getStatesAction(USStateCodes.NY, response.accessToken));
      });
    }
  }, [dispatch, instance, account, inProgress, usState]);

  const handleSubmit = (formValues: any) => {
    if (!isAuthenticated) return;

    switch (formValues.searchBy) {
      case SearchTypeFormNames.ADDRES_SEARCH: {
        if (account && inProgress === InteractionStatus.None) {
          getAccessToken(instance, account, inProgress).then((response) => {
            if (response?.accessToken)
              dispatch(searchByAddress(formValues, response.accessToken));
          });
        }
        break;
      }
      case SearchTypeFormNames.OWNER_SEARCH: {
        if (account && inProgress === InteractionStatus.None) {
          getAccessToken(instance, account, inProgress).then((response) => {
            if (response?.accessToken)
              dispatch(searchByOwner(formValues, response.accessToken));
          });
        }
        break;
      }
      case SearchTypeFormNames.DRIVERS_SEARCH: {
        if (account && inProgress === InteractionStatus.None) {
          getAccessToken(instance, account, inProgress).then((response) => {
            if (response?.accessToken)
              dispatch(
                searchByDrivers(driversData, formValues, response.accessToken),
              );
          });
        }
        break;
      }
      default: {
        return;
      }
    }
    scrollTop();
  };

  return (
    <Spin
      spinning={(loading && !usState) || detailsLoading}
      size={Sizes.LARGE}
      tip={LOADING}
    >
      <PropertySearchForm
        {...{
          usState,
          handleSubmit,
          loading,
          driversData,
          countyHob,
          cityHob,
          formValues,
        }}
      />
      {details && (
        <PropertySearchDetails searchId={result.searchId} data={details} />
      )}
      {result && <PropertySearchResultTable data={result} />}
    </Spin>
  );
};

export default PropertySearch;
